import React, { useEffect, useMemo } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  useSearchParams,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import { MoonLoader } from "react-spinners";

import apiUtils from "./apiUtils";
import { useIapUser } from "./auth/useIapUser";
import { ErrorBoundary } from "./components/ErrorBoundary";
import AppShell from "./components/standalone/AppShell";
import HomePage from "./components/standalone/HomePage";
import InvalidPatientIdNotice from "./components/standalone/InvalidPatientIdNotice";
import bbLogo from "./Blackbird_Logo.png";
import { recordRecentPatient } from "./utils/recentPatients";
import { resolvePatientName } from "./utils/patientNames";
import { isValidPatientId } from "./utils/patientId";

type MainRouteProps = {
  email: string;
  sessionId: string;
  onLogout: () => void;
};
const MainRoute: React.FC<MainRouteProps> = ({
  email,
  sessionId,
  onLogout,
}) => {
  const [searchParams] = useSearchParams();
  const patientId = searchParams.get("patient_id");
  const encounterId = searchParams.get("encounter_id");
  // A `patient_id` deep link must fail visibly on a malformed value, never
  // silently fall through to a default view as if nothing was asked
  // (ADR-073, fail-loud).
  const patientIdValid = patientId !== null && isValidPatientId(patientId);

  const currentEffectivePatient =
    patientId && patientIdValid ? patientId : null;
  if (apiUtils.getPatientId() !== currentEffectivePatient) {
    apiUtils.setPatientId(currentEffectivePatient);
  }

  useEffect(() => {
    return () => {
      apiUtils.setPatientId(null);
    };
  }, []);

  useEffect(() => {
    if (patientId && patientIdValid) {
      const name = resolvePatientName(patientId);
      recordRecentPatient(patientId, name);
    }
  }, [patientId, patientIdValid]);

  if (patientId !== null && !patientIdValid) {
    return <InvalidPatientIdNotice patientId={patientId} />;
  }

  if (patientId) {
    return (
      <AppShell
        patientId={patientId}
        encounterId={encounterId}
        email={email}
        sessionId={sessionId}
        onLogout={onLogout}
      />
    );
  }

  return <HomePage email={email} />;
};

const App: React.FC = () => {
  const { user, loading, error } = useIapUser();
  const sessionId = useMemo(() => uuidv4(), []);

  useEffect(() => {
    apiUtils.setSessionId(sessionId);
  }, [sessionId]);

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center h-screen bg-gray-50">
        <MoonLoader
          size={30}
          color="#7a46bb"
          loading={true}
          speedMultiplier={0.75}
        />
      </div>
    );
  }

  if (error || !user) {
    const isDomainError =
      !!error &&
      (error.toLowerCase().includes("blackbirdhealth.com") ||
        error.toLowerCase().includes("access denied"));

    if (isDomainError) {
      return (
        <div className="flex flex-col justify-center items-center h-screen bg-gray-50">
          <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
            <img
              className="!w-40 h-auto mb-4 object-contain inline-block"
              src={bbLogo}
              alt="Precision Care Platform Logo"
            />
            <h2 className="text-xl font-semibold text-red-600 mb-4">
              Access Denied
            </h2>
            <p className="text-gray-700 mb-6">
              Access is restricted to authorized @blackbirdhealth.com accounts.
              Please switch to your Blackbird Health account or contact IT
              support.
            </p>
            <div className="text-sm text-gray-500 mb-6">{error}</div>
            <a
              href="/api/auth/login?prompt=select_account"
              className="inline-block px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              Switch Account
            </a>
          </div>
        </div>
      );
    }

    if (!user && !error) {
      return (
        <div className="flex flex-col justify-center items-center h-screen bg-gray-50">
          <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
            <img
              className="!w-40 h-auto mb-4 object-contain inline-block"
              src={bbLogo}
              alt="Precision Care Platform Logo"
            />
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Sign in with Blackbird Health
            </h2>
            <p className="text-gray-600 mb-6">
              Precision Care Platform requires a @blackbirdhealth.com account.
            </p>
            <a
              href="/api/auth/login"
              className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium transition-colors"
            >
              Sign in with Google
            </a>
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col justify-center items-center h-screen bg-gray-50">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
          <img
            className="!w-40 h-auto mb-4 object-contain inline-block"
            src={bbLogo}
            alt="Precision Care Platform Logo"
          />
          <h2 className="text-xl font-semibold text-red-600 mb-4">
            Authentication Error
          </h2>
          <p className="text-gray-700 mb-6">
            Unable to load your profile. Try refreshing or contact IT.
          </p>
          <div className="text-sm text-gray-500 mb-4">{error}</div>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            Refresh
          </button>
        </div>
      </div>
    );
  }

  const handleLogout = () => {
    fetch("/api/auth/logout", { method: "POST" }).finally(() => {
      window.location.reload();
    });
  };

  return (
    <ErrorBoundary>
      <ToastContainer
        position="bottom-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        className="custom-toast-body"
      />
      <BrowserRouter>
        <Routes>
          <Route
            path="/*"
            element={
              <MainRoute
                email={user.email}
                sessionId={sessionId}
                onLogout={handleLogout}
              />
            }
          />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  );
};

export default App;
